/**
 * ModelRequestResponseUser Component - User-Focused Version
 *
 * Shows tool calls in a user-friendly way:
 * - Explanation of why the tool was called
 * - Tool name with icon/description
 * - Result summary (not full raw data)
 *
 * Uses the reactive GraphStore pattern:
 * - useNode(modelRequest.model_response) for reactive access to response
 * - useNodes(modelResponse.parts) for reactive access to parts
 * - No manual DAG reconstruction needed
 */

import React from 'react';
import { Brain, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  useChatTaskId,
  useChatTaskIsWorking,
} from '@sourcepilot/ui/framework/ui/molecules/chat-task-context';
import { ToolCallPartRendererUser } from '@sourcepilot/ui/shell/chat/tool-call-part-renderer-user';
import { TextPartRenderer } from '@sourcepilot/ui/shell/chat/TextPartRenderer';
import { ThinkingPartRenderer } from '@sourcepilot/ui/shell/chat/ThinkingPartRenderer';
import { ToolCallPartReadAPISchema as ToolCallPart } from '@sourcepilot/generated/chat/types/toolcallpart';
import { TextPartReadAPISchema as TextPart } from '@sourcepilot/generated/chat/types/textpart';
import { ThinkingPartReadAPISchema as ThinkingPart } from '@sourcepilot/generated/chat/types/thinkingpart';
import { ModelRequest } from '@sourcepilot/generated/chat/api/modelrequest';
import { useNodes, useNode } from '@sourcepilot/framework/relationship-manager';
import { useReactiveNode } from '@sourcepilot/ui/framework/data/hooks/use-reactive-node';
import {
  RETRYABLE_TASK_STATUSES,
  trailingResponseState,
} from '@sourcepilot/ui/framework/ui/molecules/trailing-response-state';
import { z } from 'zod';

/** Task statuses that ended the turn without a clean finish — the turn is
 *  resumable via POST /tasks/{id}/retry. */
/** Amber "stopped — Retry" notice for a task in a retryable terminal
 *  status. Rendered by ChatView (task-status-driven, so it covers
 *  failures that struck BETWEEN responses and left no incomplete
 *  ModelResponse to hang a notice on) and reused by the response-level
 *  fallback below. POSTs /tasks/{id}/retry; the continuation streams
 *  back over the normal channels, flipping the task out of the
 *  retryable status, which unmounts this notice. */
export function TaskRetryNotice({ taskId }: { taskId: string | null }) {
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleRetry = React.useCallback(async () => {
    if (!taskId || isRetrying) return;
    setIsRetrying(true);
    const apiBase = process.env.NEXT_PUBLIC_SOURCEPILOT_URL || '';
    try {
      await fetch(`${apiBase}/tasks/${taskId}/retry`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.warn('Retry request failed:', err);
      setIsRetrying(false);
    }
  }, [taskId, isRetrying]);

  return (
    <div className="flex items-center gap-2 p-2 text-sm text-amber-600 italic">
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>The model stopped before finishing.</span>
      {taskId && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          className="inline-flex items-center gap-1 not-italic font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800 disabled:opacity-60 disabled:no-underline"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
          {isRetrying ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </div>
  );
}

interface ModelRequestResponseUserProps {
  modelRequest: ModelRequest;
  /** True only for the very last response in the chat (last request of the
   *  last step). The activity / "stopped — Retry" indicator renders only then. */
  isLastResponse?: boolean;
}

export function ModelRequestResponseUser({
  modelRequest,
  isLastResponse = false,
}: ModelRequestResponseUserProps) {
  // REACTIVE: Get model response from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  const modelResponse = useNode(modelRequest.model_response);

  if (!modelResponse) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <ModelResponsePartsUser
        modelResponse={modelResponse}
        isLastResponse={isLastResponse}
      />
    </div>
  );
}

/**
 * Helper component to reactively access parts for a model response (user view)
 */
function ModelResponsePartsUser({
  modelResponse,
  isLastResponse = false,
}: {
  modelResponse: any;
  isLastResponse?: boolean;
}) {
  // REACTIVE: Get response parts from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  // Apply client-side ordering by order_index (memoized internally)
  const sortedParts = useNodes(modelResponse.parts).orderBy('order_index').value();

  // Visible waiting indicator while the model response hasn't finished,
  // so the user sees activity even when thinking tokens are the only
  // in-flight content (or when the provider hasn't sent any part yet).
  const isResponseComplete = modelResponse?.is_complete === true;
  const hasStreamingPart = sortedParts.some((p: any) => p?.is_streaming === true);
  const latestStreamingPart = [...sortedParts].reverse().find((p: any) => p?.is_streaming === true) as any;
  const latestStreamingIsThinking =
    latestStreamingPart && (latestStreamingPart?._label || latestStreamingPart?.type) === 'ThinkingPart';

  // Retry for a turn the server left `interrupted` (killed mid-stream by a
  // restart/crash) WITH an incomplete trailing response. The task-status-
  // driven notice lives in ChatView (see TaskRetryNotice) — it covers
  // failures that struck between responses too; this response-level
  // fallback stands down for those (see `trailingResponseState`) so the two
  // never double-render.
  const taskId = useChatTaskId();
  const task = useReactiveNode<any>(taskId);
  const taskIsWorking = useChatTaskIsWorking();
  const trailing = trailingResponseState({
    isLastResponse,
    isResponseComplete,
    isStreaming: modelResponse?.is_streaming === true,
    taskIsWorking,
    taskStatus: task?.status,
  });

  return (
    <div className="flex flex-col">
      {sortedParts.map((part: any) => {
        // Use _label instead of type (our ORM convention)
        const partType = (part as any)?._label || (part as any)?.type;

        // Render text parts (LLM responses)
        if (partType === 'TextPart') {
          return (
            <TextPartRenderer
              key={part.id}
              part={part as z.infer<typeof TextPart>}
              isStreaming={part?.is_streaming || false}
            />
          );
        }

        // Render tool calls (respecting show_in_ui flag)
        if (partType === 'ToolCallPart') {
          return (
            <ToolCallPartRendererUser
              key={part.id}
              part={part as z.infer<typeof ToolCallPart>}
              isStreaming={part?.is_streaming || false}
            />
          );
        }

        // Render thinking (reasoning) parts — collapsible card.
        if (partType === 'ThinkingPart') {
          return (
            <ThinkingPartRenderer
              key={part.id}
              part={part as z.infer<typeof ThinkingPart>}
              isStreaming={part?.is_streaming || false}
            />
          );
        }
        return null;
      })}

      {/* Activity indicator — ONLY on the last response in the chat. While
          in-flight (is_streaming) show a spinner; a last response that is
          neither complete nor streaming ended terminally (error / timeout) —
          show the retryable notice, UNLESS the task-status-driven notice in
          ChatView is already showing. Gating on
          isLastResponse stops a buried incomplete response (e.g. a
          continuation abandoned mid-turn) from rendering a stray spinner or
          Retry prompt scrolled up in history. */}
      {trailing !== 'idle' &&
        (trailing === 'working' ? (
          <div className="flex items-center gap-2 p-2 text-sm text-gray-500 italic">
            {latestStreamingIsThinking ? (
              <>
                <Brain className="w-4 h-4 animate-pulse" />
                <span>Thinking…</span>
              </>
            ) : hasStreamingPart ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Responding…</span>
              </>
            ) : (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Waiting for model…</span>
              </>
            )}
          </div>
        ) : (
          <TaskRetryNotice taskId={taskId} />
        ))}
    </div>
  );
}

export { RETRYABLE_TASK_STATUSES, trailingResponseState };
